import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";
import {
  doc, updateDoc, getDoc, collection, query, where, getDocs, orderBy
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const AVAILABLE_SPORTS = ["탁구", "배드민턴", "테니스", "볼링", "축구", "농구", "배구", "야구"];

function AssociationProfile() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  
  const [profile, setProfile] = useState({
    associationName: "",
    businessNumber: "",
    associationAddress: "",
    representativeName: "",
    sports: [],
    phone: "",
    email: "",
    description: "",
    website: "",
    establishedYear: "",
    profileImg: ""
  });
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [stats, setStats] = useState({
    totalTournaments: 0,
    activeTournaments: 0,
    totalParticipants: 0,
    completedTournaments: 0
  });
  const [recentTournaments, setRecentTournaments] = useState([]);

  useEffect(() => {
    if (!user || user.userType !== 'association') {
      navigate('/');
      return;
    }
    fetchProfileData();
  }, [user, navigate]);

  const fetchProfileData = async () => {
    try {
      // 프로필 정보 가져오기
      const docRef = doc(db, "users", user.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setProfile({
          associationName: data.associationName || "",
          businessNumber: data.businessNumber || "",
          associationAddress: data.associationAddress || "",
          representativeName: data.representativeName || "",
          sports: data.sports || [],
          phone: data.phone || "",
          email: data.email || "",
          description: data.description || "",
          website: data.website || "",
          establishedYear: data.establishedYear || "",
          profileImg: data.profileImg || ""
        });
      }

      // 통계 데이터 가져오기 - orderBy 제거하고 조건문으로 처리
      try {
        const tournamentsQuery = query(
          collection(db, "tournaments"),
          where("organizerId", "==", user.id)
        );
        const tournamentsSnap = await getDocs(tournamentsQuery);
        const tournaments = tournamentsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // 최신순으로 정렬
        tournaments.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bDate - aDate;
        });

        const totalTournaments = tournaments.length;
        const activeTournaments = tournaments.filter(t => t.status === 'active' || t.status === 'registration').length;
        const completedTournaments = tournaments.filter(t => t.status === 'completed').length;
        const totalParticipants = tournaments.reduce((sum, t) => sum + (t.participants?.length || 0), 0);

        setStats({
          totalTournaments,
          activeTournaments,
          completedTournaments,
          totalParticipants
        });

        // 최근 대회 3개
        setRecentTournaments(tournaments.slice(0, 3));

      } catch (tournamentError) {
        console.error("대회 데이터 로딩 실패:", tournamentError);
        // 통계 기본값 유지
      }

    } catch (error) {
      console.error("프로필 데이터 로딩 실패:", error);
      setMsg("프로필 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSportToggle = (sport) => {
    setProfile(prev => ({
      ...prev,
      sports: prev.sports.includes(sport)
        ? prev.sports.filter(s => s !== sport)
        : [...prev.sports, sport]
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // 기존 이미지 삭제
      if (profile.profileImg && profile.profileImg.includes("firebase")) {
        try {
          const path = profile.profileImg.split("/o/")[1].split("?")[0];
          await deleteObject(ref(storage, decodeURIComponent(path)));
        } catch (err) {
          console.log("기존 이미지 삭제 실패:", err);
        }
      }

      // 새 이미지 업로드
      const storageRef = ref(storage, `associations/${user.id}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      setProfile(prev => ({ ...prev, profileImg: url }));
      setMsg("프로필 이미지가 업로드되었습니다.");

    } catch (error) {
      console.error("이미지 업로드 실패:", error);
      setMsg("이미지 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setMsg("");
    
    if (!profile.associationName.trim()) {
      setMsg("협회명을 입력해주세요.");
      return;
    }
    
    if (!profile.representativeName.trim()) {
      setMsg("대표자명을 입력해주세요.");
      return;
    }
    
    if (profile.sports.length === 0) {
      setMsg("최소 1개 이상의 종목을 선택해주세요.");
      return;
    }

    try {
      await updateDoc(doc(db, "users", user.id), {
        ...profile,
        updatedAt: new Date()
      });

      // 로컬스토리지 업데이트
      const updatedUser = {
        ...user,
        associationName: profile.associationName,
        email: profile.email,
        phone: profile.phone
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      setEditing(false);
      setMsg("프로필이 성공적으로 업데이트되었습니다!");

    } catch (error) {
      console.error("프로필 저장 실패:", error);
      setMsg("프로필 저장 중 오류가 발생했습니다.");
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'registration': { label: '모집중', color: '#10b981', bg: '#d1fae5' },
      'active': { label: '진행중', color: '#3b82f6', bg: '#dbeafe' },
      'completed': { label: '완료', color: '#6b7280', bg: '#f3f4f6' }
    };
    const config = statusConfig[status] || statusConfig.registration;
    
    return (
      <span style={{
        background: config.bg,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        border: `1px solid ${config.color}30`
      }}>
        {config.label}
      </span>
    );
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    fontSize: "16px",
    transition: "all 0.3s ease",
    background: "#fff",
    boxSizing: "border-box"
  };

  const focusStyle = {
    borderColor: "#667eea",
    boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)"
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        🏢 협회 프로필 로딩중...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '40px auto',
      padding: '0 20px'
    }}>
      {/* 메시지 */}
      {msg && (
        <div style={{
          background: msg.includes('실패') || msg.includes('오류') ? '#fee2e2' : '#d1fae5',
          color: msg.includes('실패') || msg.includes('오류') ? '#dc2626' : '#047857',
          padding: '12px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          fontSize: '14px',
          fontWeight: '600',
          border: `1px solid ${msg.includes('실패') || msg.includes('오류') ? '#fca5a5' : '#86efac'}`
        }}>
          {msg}
        </div>
      )}

      {/* 헤더 */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '24px',
        padding: '40px 32px',
        color: '#fff',
        marginBottom: '32px',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{
              fontSize: '36px',
              fontWeight: '900',
              margin: '0',
              marginBottom: '8px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              🏢 {profile.associationName || '협회 프로필'}
            </h1>
            <p style={{
              fontSize: '18px',
              margin: '0',
              opacity: '0.9'
            }}>
              협회 정보를 관리하고 업데이트하세요
            </p>
          </div>
          
          <button
            onClick={() => setEditing(!editing)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)'
            }}
            onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
          >
            {editing ? '❌ 취소' : '✏️ 수정'}
          </button>
        </div>

        {/* 통계 요약 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '20px',
          opacity: '0.9'
        }}>
          <div>
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>총 대회 수</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.totalTournaments}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>진행중 대회</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.activeTournaments}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>총 참가자</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.totalParticipants}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>완료 대회</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{stats.completedTournaments}</div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '32px'
      }}>
        {/* 메인 프로필 */}
        <div>
          {/* 기본 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '24px',
              color: '#1f2937',
              borderBottom: '2px solid #f3f4f6',
              paddingBottom: '12px'
            }}>
              🏢 기본 정보
            </h3>

            {/* 프로필 이미지 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '20px',
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '3px solid #e5e7eb',
                position: 'relative'
              }}>
                {profile.profileImg ? (
                  <img
                    src={profile.profileImg}
                    alt="협회 로고"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span style={{ fontSize: '32px', color: '#9ca3af' }}>🏢</span>
                )}
                
                {editing && (
                  <label style={{
                    position: 'absolute',
                    bottom: '-5px',
                    right: '-5px',
                    background: '#667eea',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}>
                    📷
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
              
              {uploading && (
                <div style={{ color: '#667eea', fontSize: '14px' }}>
                  이미지 업로드 중...
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  협회명 *
                </label>
                <input
                  type="text"
                  value={profile.associationName}
                  onChange={(e) => handleInputChange("associationName", e.target.value)}
                  disabled={!editing}
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                  onFocus={(e) => editing && Object.assign(e.target.style, focusStyle)}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  사업자등록번호
                </label>
                <input
                  type="text"
                  value={profile.businessNumber}
                  onChange={(e) => handleInputChange("businessNumber", e.target.value)}
                  disabled={!editing}
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                대표자명 *
              </label>
              <input
                type="text"
                value={profile.representativeName}
                onChange={(e) => handleInputChange("representativeName", e.target.value)}
                disabled={!editing}
                style={{
                  ...inputStyle,
                  opacity: editing ? '1' : '0.7'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  연락처
                </label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  disabled={!editing}
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  이메일
                </label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  disabled={!editing}
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                협회 주소
              </label>
              <input
                type="text"
                value={profile.associationAddress}
                onChange={(e) => handleInputChange("associationAddress", e.target.value)}
                disabled={!editing}
                style={{
                  ...inputStyle,
                  opacity: editing ? '1' : '0.7'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  웹사이트
                </label>
                <input
                  type="url"
                  value={profile.website}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  disabled={!editing}
                  placeholder="https://..."
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  설립연도
                </label>
                <input
                  type="number"
                  value={profile.establishedYear}
                  onChange={(e) => handleInputChange("establishedYear", e.target.value)}
                  disabled={!editing}
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{
                    ...inputStyle,
                    opacity: editing ? '1' : '0.7'
                  }}
                />
              </div>
            </div>
          </div>

          {/* 상세 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '24px',
              color: '#1f2937',
              borderBottom: '2px solid #f3f4f6',
              paddingBottom: '12px'
            }}>
              📝 상세 정보
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                협회 소개
              </label>
              <textarea
                value={profile.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                disabled={!editing}
                rows={4}
                placeholder="협회에 대한 상세한 소개를 입력하세요..."
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  opacity: editing ? '1' : '0.7'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                취급 종목 *
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                padding: '16px',
                background: editing ? '#f8fafc' : '#f3f4f6',
                borderRadius: '12px',
                border: '2px solid #e1e5e9'
              }}>
                {AVAILABLE_SPORTS.map(sport => (
                  <button
                    key={sport}
                    type="button"
                    onClick={() => editing && handleSportToggle(sport)}
                    disabled={!editing}
                    style={{
                      padding: '8px 12px',
                      background: profile.sports.includes(sport) ? '#667eea' : '#fff',
                      color: profile.sports.includes(sport) ? '#fff' : '#6b7280',
                      border: `2px solid ${profile.sports.includes(sport) ? '#667eea' : '#dee2e6'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: editing ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      opacity: editing ? '1' : '0.7'
                    }}
                  >
                    {sport}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                선택된 종목: {profile.sports.length > 0 ? profile.sports.join(", ") : "없음"}
              </div>
            </div>
          </div>

          {/* 저장 버튼 */}
          {editing && (
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setEditing(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
              <button
                onClick={handleSave}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px 32px',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                💾 저장하기
              </button>
            </div>
          )}
        </div>

        {/* 사이드바 */}
        <div>
          {/* 최근 대회 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h4 style={{
                fontSize: '18px',
                fontWeight: '700',
                margin: '0',
                color: '#1f2937'
              }}>
                🏆 최근 대회
              </h4>
              <button
                onClick={() => navigate('/association/create-tournament')}
                style={{
                  background: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                + 새 대회
              </button>
            </div>

            {recentTournaments.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
                <div style={{ fontSize: '14px' }}>아직 생성된 대회가 없습니다</div>
              </div>
            ) : (
              <div>
                {recentTournaments.map(tournament => (
                  <div
                    key={tournament.id}
                    style={{
                      padding: '12px',
                      border: '1px solid #f1f5f9',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => navigate(`/association/manage-tournament/${tournament.id}`)}
                  >
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '4px'
                    }}>
                      {tournament.name}
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      <span>{tournament.sport}</span>
                      {getStatusBadge(tournament.status)}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      marginTop: '4px'
                    }}>
                      참가자: {tournament.participants?.length || 0}명 • {formatDate(tournament.startDate)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 빠른 링크 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              🚀 빠른 링크
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { 
                  label: '대시보드', 
                  icon: '📊', 
                  path: '/association',
                  color: '#3b82f6'
                },
                { 
                  label: '새 대회 생성', 
                  icon: '➕', 
                  path: '/association/create-tournament',
                  color: '#10b981'
                },
                { 
                  label: '모든 대회 보기', 
                  icon: '🏆', 
                  path: '/tournaments',
                  color: '#f59e0b'
                },
                { 
                  label: '길드 관리', 
                  icon: '🛡️', 
                  path: '/guild-management',
                  color: '#8b5cf6'
                }
              ].map((link, index) => (
                <button
                  key={index}
                  onClick={() => navigate(link.path)}
                  style={{
                    background: `${link.color}10`,
                    color: link.color,
                    border: `1px solid ${link.color}30`,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseOver={e => {
                    e.target.style.background = `${link.color}20`;
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={e => {
                    e.target.style.background = `${link.color}10`;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <span>{link.icon}</span>
                  <span>{link.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 길드 관리 도구 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              🛡️ 길드 관리 도구
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => navigate('/guild-member-management')}
                style={{
                  background: '#e0f2fe',
                  color: '#0891b2',
                  border: '1px solid #0891b230',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                👥 멤버 관리
              </button>
              
              <button
                onClick={() => navigate('/guild-tier-management')}
                style={{
                  background: '#fef3c7',
                  color: '#d97706',
                  border: '1px solid #d9770630',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                🏅 등급 관리
              </button>
              
              <button
                onClick={() => navigate('/guild-statistics')}
                style={{
                  background: '#f3e8ff',
                  color: '#9333ea',
                  border: '1px solid #9333ea30',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                📈 통계 보기
              </button>
            </div>
          </div>

          {/* 대회 자동화 도구 */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '24px',
            color: '#fff',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#fff'
            }}>
              🤖 대회 자동화
            </h4>
            
            <p style={{
              fontSize: '14px',
              margin: '0',
              marginBottom: '16px',
              opacity: '0.9',
              lineHeight: '1.4'
            }}>
              참가 신청부터 대진표 생성까지 모든 과정을 자동화하여 대회 관리를 더욱 쉽게!
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚡</span>
                <span>자동 대진표 생성</span>
              </div>
              
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>📱</span>
                <span>실시간 결과 업데이트</span>
              </div>
              
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🏆</span>
                <span>자동 순위 산정</span>
              </div>
            </div>

            <button
              onClick={() => navigate('/tournament-automation-guide')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%',
                marginTop: '12px'
              }}
            >
              🔧 자동화 설정하기
            </button>
          </div>

          {/* 협회 설정 */}
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '16px',
              color: '#1f2937'
            }}>
              ⚙️ 협회 설정
            </h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => navigate('/association-settings')}
                style={{
                  background: '#f8fafc',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                🔧 일반 설정
              </button>
              
              <button
                onClick={() => navigate('/association-notifications')}
                style={{
                  background: '#fef7ff',
                  color: '#86198f',
                  border: '1px solid #86198f30',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                🔔 알림 설정
              </button>
              
              <button
                onClick={() => navigate('/association-billing')}
                style={{
                  background: '#eff6ff',
                  color: '#2563eb',
                  border: '1px solid #2563eb30',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                💳 요금제 관리
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssociationProfile;